import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@starbucks-web/pattern-library/lib/components/button';
import Icon from '@starbucks-web/pattern-library/lib/components/icon';
import { useModalContext } from '@starbucks-web/pattern-library/lib/components/modal-provider';
import warningSolid from '@starbucks-web/pattern-library/lib/icons/warning-solid';
import pin from '@starbucks-web/pattern-library/lib/icons/pin';
import ViewportChecker from '@starbucks-web/pattern-library/lib/components/viewport-checker';

import { signedInSelector } from 'shared/app/bundles/user';

import { showOutOfStockSimilarItemsCtaSelector } from 'ordering/app/state/selectors';
import SimilarItemsOverlay, {
  similarItemsOverlayHeadingId,
  similarItemsOverlayId,
} from 'ordering/app/components/shared/similar-items-overlay';
import { trackAvailabilityStaleness } from 'ordering/app/util/new-relic-logging';
import { usePdpContext } from '../pdp-provider';

import messages from './messages';
import sharedMessages from 'ordering/app/messages';

const getIcon = (availability, store) => {
  if (availability === 'not orderable')
    return <Icon className="color-red mr2" path={warningSolid} />;

  if ((store && availability === 'available') || !store)
    return (
      <Icon
        className={`${!store ? 'color-black60' : 'color-black'} mr2`}
        path={pin}
      />
    );

  return <Icon className="color-red mr2" path={warningSolid} />;
};

const getMessage = (availability, store) => {
  if (availability === 'notorderable')
    return (
      <span className="text-bold" data-e2e="product-unavailable-message">
        <FormattedMessage {...messages.notOrderable} />
      </span>
    );

  if (!store)
    return (
      <span
        className="color-textBlackSoft"
        data-e2e="store-not-selected-message"
      >
        <FormattedMessage {...messages.selectStoreForAvailability} />
      </span>
    );

  if (availability === 'available')
    return (
      <FormattedMessage
        {...messages.available}
        values={{ store: store.name }}
      />
    );

  if (availability === 'temporarilyunavailable')
    return (
      <span className="text-bold" data-e2e="product-unavailable-message">
        <FormattedMessage
          {...messages.temporarilyUnavailable}
          values={{ store: store.name }}
        />
      </span>
    );

  if (availability === 'notavailablehere')
    return (
      <span className="text-bold" data-e2e="product-unavailable-message">
        <FormattedMessage
          {...messages.unavailableHere}
          values={{ store: store.name }}
        />
      </span>
    );

  return (
    <span className="text-bold" data-e2e="product-unavailable-message">
      <FormattedMessage {...messages.unavailable} />
    </span>
  );
};

const AvailabilityStatus = ({ className }) => {
  const dispatch = useDispatch();
  const isSignedIn = useSelector(signedInSelector);

  const {
    product,
    selectedStore,
    sizeInfo: { selectedSize },
  } = usePdpContext();

  const { store } = selectedStore ?? {};
  const { openModal } = useModalContext();
  const showOutOfStockSimilarItemsCta = useSelector(
    showOutOfStockSimilarItemsCtaSelector
  );
  const openSimilarItemsOverlay = () => {
    openModal({
      ariaLabelledBy: similarItemsOverlayHeadingId,
      component: SimilarItemsOverlay,
      componentProps: {
        outOfStockItem: {
          formCode: product?.formCode,
          productNumber: product?.productNumber,
          sizeCode: selectedSize?.sizeCode,
        },
        source: 'pdp',
      },
      id: similarItemsOverlayId,
    });
  };

  useEffect(() => {
    dispatch(trackAvailabilityStaleness());
  }, []);

  // This null check is purposefully right before the main return
  // to avoid React complaining about inconsistent number of hooks
  // when this condition is hit.
  if (product?.error || !product?.productOptions) {
    return null;
  }

  const availabilityState = product?.availability?.toLowerCase();
  return (
    <div className={className}>
      <div className="flex items-center" data-e2e="availabilityStatus">
        {getIcon(availabilityState, store)}{' '}
        {getMessage(availabilityState, store)}
      </div>
      {availabilityState !== 'available' &&
        isSignedIn &&
        store &&
        showOutOfStockSimilarItemsCta && (
          <ViewportChecker percentVisible={50} unobserveOnIntersection>
            {({ elementRef }) => {
              return (
                <Button
                  className="mt3 ml5"
                  onClick={openSimilarItemsOverlay}
                  ref={elementRef}
                >
                  <FormattedMessage {...sharedMessages.similarItemsCta} />
                </Button>
              );
            }}
          </ViewportChecker>
        )}
    </div>
  );
};

export default AvailabilityStatus;
